/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import StyleguideView from "@/src/styleguide/components/StyleguideView/StyleguideView.tsx";
import Accordion, { AccordionProps } from "@/src/components/Accordion/Accordion.tsx";
import LinkLegalbird from "@/src/components/legalbird/LinkLegalbird/LinkLegalbird.tsx";
import { ArrowForward } from "@mui/icons-material";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import AccordionGroupProvider from "@/src/components/Accordion/AccordionGroupProvider.tsx";
import IconTrafficLaw from "@/src/assets/iconsRedesign2026/traffic/toggleable/IconTrafficLaw.tsx";
import Grid from "@mui/material/Grid";

export default function AccordionStyleguide() {
  return (
    <>
      <StyleguideView title={"Outlined"}>
        <AccordionExamplesOutlined />
      </StyleguideView>
      <StyleguideView title={"Outlined Group"}>
        <AccordionGroupProvider>
          <AccordionExamplesOutlined />
        </AccordionGroupProvider>
      </StyleguideView>
      <StyleguideView title={"Contained"}>
        <AccordionExamplesContained variant={"contained"} icon={IconTrafficLaw} />
      </StyleguideView>
      <StyleguideView title={"Contained Group"}>
        <AccordionGroupProvider>
          <AccordionExamplesContained variant={"contained"} icon={IconTrafficLaw} />
        </AccordionGroupProvider>
      </StyleguideView>
      <StyleguideView title={"Text"}>
        <AccordionExamplesText />
      </StyleguideView>
      <StyleguideView title={"Text Group"}>
        <AccordionGroupProvider>
          <AccordionExamplesText />
        </AccordionGroupProvider>
      </StyleguideView>
    </>
  );
}

function AccordionExamplesOutlined() {
  return (
    <>
      <Stack spacing={"1rem"}>
        <Accordion icon={IconTrafficLaw} title={"Lorem Ipsum dolor"}>
          <Stack spacing={"1rem"}>
            <Typography>
              Lorem ipsum dolor sit amet consectetur. In sollicitudin diam mi turpis elementum. Ipsum proin at turpis dui tempor pellentesque sit pretium mi.
            </Typography>
            <Box>
              <LinkLegalbird to={"#"} standalone>
                Mehr erfahren <ArrowForward />
              </LinkLegalbird>
            </Box>
          </Stack>
        </Accordion>
        <Accordion icon={IconTrafficLaw} title={"Lorem Ipsum dolor test amet"}>
          <Typography>Lorem ipsum dolor sit amet consectetur. In sollicitudin diam mi</Typography>
        </Accordion>
        <Accordion icon={IconTrafficLaw} title={"Ipsum dolor test amet"}>
          <Typography>Lorem ipsum dolor sit amet consectetur. In sollicitudin diam mi</Typography>
        </Accordion>
      </Stack>
    </>
  );
}

function AccordionExamplesContained({ variant, icon }: Pick<AccordionProps, "variant" | "icon">) {
  return (
    <>
      <Stack spacing={"1rem"}>
        <Accordion variant={variant} icon={icon} title={"Lorem Ipsum dolor"}>
          <AccordionExamplesContainedContent />
        </Accordion>
        <Accordion variant={variant} icon={icon} title={"Lorem Ipsum dolor test amet"}>
          <AccordionExamplesContainedContent />
        </Accordion>
        <Accordion variant={variant} icon={icon} title={"Ipsum dolor test amet"}>
          <AccordionExamplesContainedContent />
        </Accordion>
      </Stack>
    </>
  );
}

function AccordionExamplesContainedContent() {
  return (
    <>
      <Grid container spacing={"1rem"}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper sx={{ padding: "1.5rem", height: "100%" }}>
            <Stack spacing={"1rem"}>
              <Typography variant={"button"}>Lorem dolor</Typography>
              <Typography>Lorem ipsum dolor sit amet consectetur. In sollicitudin diam mi</Typography>
              <Box>
                <Button variant={"contained"} endIcon={<ArrowForward />}>
                  Jetzt starten
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper sx={{ padding: "1.5rem", height: "100%" }}>
            <Stack spacing={"1rem"}>
              <Typography variant={"button"}>Lorem ipsum dolor</Typography>
              <Box>
                <Button variant={"contained"} endIcon={<ArrowForward />}>
                  Jetzt starten
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper sx={{ padding: "1.5rem", height: "100%" }}>
            <Stack spacing={"1rem"}>
              <Typography variant={"button"}>Test</Typography>
              <Typography>Lorem ipsum dolor sit amet consectetur. In sollicitudin diam mi</Typography>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}

function AccordionExamplesText() {
  return (
    <>
      <Stack spacing={"1rem"}>
        <Accordion variant={"text"} title={"Lorem Ipsum dolor"} titlePrefix={"01"}>
          <Stack spacing={"1rem"}>
            <Typography>
              Lorem ipsum dolor sit amet consectetur. In sollicitudin diam mi turpis elementum. Ipsum proin at turpis dui tempor pellentesque sit pretium mi.
            </Typography>
            <Box>
              <LinkLegalbird to={"#"} standalone>
                Mehr erfahren <ArrowForward />
              </LinkLegalbird>
            </Box>
          </Stack>
        </Accordion>
        <Accordion variant={"text"} title={"Lorem Ipsum dolor test amet"} titlePrefix={"02"}>
          <Typography>
            Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam
            voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit
            amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed
            diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit
            amet.
          </Typography>
        </Accordion>
        <Accordion variant={"text"} title={"Ipsum dolor test amet"} titlePrefix={"03"}>
          <Typography>
            Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam
            voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit
            amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy
          </Typography>
        </Accordion>
      </Stack>
    </>
  );
}
